import React from 'react';

/**
 * Top-level React error boundary. Without this, any uncaught render-time
 * error blanks out the entire SPA. Here we catch those errors, render a
 * branded fallback with a "Try again" button, and log the stack to the
 * browser console so Sentry-style collectors (or user bug reports) can
 * still recover the underlying cause.
 *
 * Only class components can implement componentDidCatch today — this is
 * the one place we intentionally break the "function components only" rule.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Keep this even in production — `console.error` is NOT stripped by the
    // craco transform-remove-console plugin and surfaces real crashes.
    console.error('[ErrorBoundary] caught render error:', error, info?.componentStack);
  }

  handleReload = () => {
    // Hard reload so any corrupted client state (stale cache, partially
    // hydrated DOM) is thrown away. Safer than trying to reset state in
    // place for an unknown error class.
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main
        className="min-h-screen flex flex-col items-center justify-center px-5 py-20 text-center"
        style={{
          fontFamily: "'Outfit', sans-serif",
          background: '#ffffff',
        }}
        data-testid="error-boundary-fallback"
      >
        <span
          className="text-[72px] sm:text-[96px] font-bold leading-none mb-2"
          style={{ color: '#1a4d7a', letterSpacing: '-0.04em' }}
          aria-hidden="true"
        >
          ⚠
        </span>
        <h1
          className="text-3xl sm:text-4xl font-bold mb-3"
          style={{ color: '#1a4d7a' }}
        >
          Something went wrong
        </h1>
        <p
          className="text-base sm:text-lg max-w-[520px] mb-8"
          style={{ color: '#2d5f8d' }}
        >
          Sorry — an unexpected error occurred while loading this page.
          Try reloading to continue exploring Sitges.
        </p>
        <button
          onClick={this.handleReload}
          data-testid="error-boundary-reload-button"
          className="h-12 px-8 rounded-full bg-[#3c84ff] hover:bg-[#2a6fe0] text-white text-sm font-semibold transition-colors active:scale-95"
        >
          Reload page
        </button>
      </main>
    );
  }
}

export default ErrorBoundary;
